import { useEffect, useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { toysApi, ordersApi, api } from "@/lib/api";
import { Plus, Pencil, Trash2, RefreshCw, X, Lock, LogOut, ImagePlus, Wand2, Loader2, Printer, Eye } from "lucide-react";
import { toast } from "sonner";
import CertificatePreview from "@/components/CertificatePreview";

const ADMIN_USER = "lili";
const ADMIN_PASS = "1234";
const ADMIN_AUTH_KEY = "lili-admin-auth";

const AdminLogin = ({ onSuccess }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (username === ADMIN_USER && password === ADMIN_PASS) {
      sessionStorage.setItem(ADMIN_AUTH_KEY, "1");
      setError("");
      onSuccess();
    } else {
      setError("Identifiants incorrects");
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="admin-login">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-cream/30 border border-border rounded-2xl p-8 space-y-5"
      >
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-terracotta text-background grid place-items-center">
            <Lock size={16} strokeWidth={1.5} />
          </span>
          <div>
            <h1 className="font-serif-display text-2xl text-ink leading-none">Connexion</h1>
            <p className="font-script text-camel text-base">espace atelier</p>
          </div>
        </div>

        <label className="block">
          <span className="block text-xs text-ink/70 mb-1.5 font-body">Utilisateur</span>
          <input
            data-testid="admin-username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            className="w-full bg-background border border-border rounded-lg px-4 py-2.5 font-body text-sm outline-none focus:border-terracotta"
          />
        </label>
        <label className="block">
          <span className="block text-xs text-ink/70 mb-1.5 font-body">Mot de passe</span>
          <input
            data-testid="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-background border border-border rounded-lg px-4 py-2.5 font-body text-sm outline-none focus:border-terracotta"
          />
        </label>

        {error && (
          <p data-testid="admin-login-error" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <button
          type="submit"
          data-testid="admin-login-submit"
          className="w-full bg-terracotta text-background rounded-full py-2.5 font-body text-sm hover:shadow-warm transition-all"
        >
          Se connecter
        </button>
      </form>
    </div>
  );
};

const blankToy = {
  name: "",
  species_fr: "",
  species_en: "",
  price: 0,
  colors: "",
  creation_hours: 10,
  gallery: [],
  description_fr: "",
  description_en: "",
  personality_fr: "",
  personality_en: "",
  birth_date: "",
  available: true,
  featured: false,
};

const Admin = () => {
  const { t } = useLanguage();
  const [authed, setAuthed] = useState(() => sessionStorage.getItem(ADMIN_AUTH_KEY) === "1");

  if (!authed) {
    return <AdminLogin onSuccess={() => setAuthed(true)} />;
  }

  const logout = () => {
    sessionStorage.removeItem(ADMIN_AUTH_KEY);
    setAuthed(false);
  };

  return <AdminDashboard t={t} onLogout={logout} />;
};

const AdminDashboard = ({ t, onLogout }) => {
  const [tab, setTab] = useState("toys");
  const [toys, setToys] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);

  const loadAll = async () => {
    const [tts, ors] = await Promise.all([toysApi.list(), ordersApi.list()]);
    setToys(tts);
    setOrders(ors);
  };

  useEffect(() => {
    let active = true;
    Promise.all([toysApi.list(), ordersApi.list()]).then(([tts, ors]) => {
      if (!active) return;
      setToys(tts);
      setOrders(ors);
    });
    return () => {
      active = false;
    };
  }, []);

  const openCreate = () => {
    setEditing(null);
    setShowForm(true);
  };

  const openEdit = (toy) => {
    setEditing({
      ...toy,
      colors: (toy.colors || []).join(", "),
      gallery: toy.gallery || [],
    });
    setShowForm(true);
  };

  const remove = async (toy) => {
    if (!window.confirm(t.admin.confirmDelete)) return;
    await toysApi.remove(toy.id);
    toast.success("OK");
    loadAll();
  };

  const relist = async (toy) => {
    await api.post(`/admin/reset-toy/${toy.id}`);
    toast.success("OK");
    loadAll();
  };

  const onSave = async (data) => {
    const gallery = Array.isArray(data.gallery)
      ? data.gallery
      : (data.gallery || "").split(",").map((s) => s.trim()).filter(Boolean);
    const payload = {
      ...data,
      price: parseFloat(data.price) || 0,
      creation_hours: parseInt(data.creation_hours, 10) || 0,
      colors: (data.colors || "").split(",").map((s) => s.trim()).filter(Boolean),
      gallery: gallery,
    };
    if (editing?.id) {
      await toysApi.update(editing.id, payload);
    } else {
      await toysApi.create(payload);
    }
    toast.success("OK");
    setShowForm(false);
    setEditing(null);
    loadAll();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16" data-testid="admin-page">
      <div className="flex items-start justify-between gap-6">
        <div>
          <p className="font-script text-camel text-2xl">{t.admin.subtitle}</p>
          <h1 className="font-serif-display text-5xl text-ink mt-1">{t.admin.title}</h1>
        </div>
        <button
          onClick={onLogout}
          data-testid="admin-logout"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border hover:border-terracotta text-xs uppercase tracking-widest"
        >
          <LogOut size={14} strokeWidth={1.5} />
          Déconnexion
        </button>
      </div>

      <div className="mt-10 flex gap-2 border-b border-border">
        {[
          { key: "toys", label: t.admin.tabsToys },
          { key: "orders", label: t.admin.tabsOrders },
        ].map((opt) => (
          <button
            key={opt.key}
            onClick={() => setTab(opt.key)}
            data-testid={`admin-tab-${opt.key}`}
            className={`px-5 py-3 font-body text-sm tracking-wide -mb-px border-b-2 ${
              tab === opt.key
                ? "border-terracotta text-terracotta"
                : "border-transparent text-muted-foreground hover:text-ink"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {tab === "toys" ? (
        <div className="mt-8">
          <div className="flex justify-end mb-6">
            <button
              onClick={openCreate}
              data-testid="admin-add-toy"
              className="inline-flex items-center gap-2 bg-terracotta text-background px-5 py-2.5 rounded-full text-sm hover:shadow-warm transition-all"
            >
              <Plus size={14} /> {t.admin.addToy}
            </button>
          </div>

          <div className="border border-border rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-cream/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-5 py-3">{t.admin.name}</th>
                  <th className="px-5 py-3">{t.admin.species}</th>
                  <th className="px-5 py-3">{t.admin.price}</th>
                  <th className="px-5 py-3">{t.common.available}</th>
                  <th className="px-5 py-3 text-right">—</th>
                </tr>
              </thead>
              <tbody>
                {toys.filter((tt) => tt.available).map((toy) => (
                  <tr key={toy.id} className="border-t border-border text-sm">
                    <td className="px-5 py-4 font-serif-display text-lg text-ink">{toy.name}</td>
                    <td className="px-5 py-4 text-muted-foreground">{toy.species_fr}</td>
                    <td className="px-5 py-4 text-terracotta">${toy.price}</td>
                    <td className="px-5 py-4">
                      <span className="text-moss">●</span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => openEdit(toy)}
                          data-testid={`admin-edit-${toy.id}`}
                          className="p-2 rounded-full hover:bg-cream"
                        >
                          <Pencil size={14} strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => remove(toy)}
                          data-testid={`admin-delete-${toy.id}`}
                          className="p-2 rounded-full hover:bg-cream text-destructive"
                        >
                          <Trash2 size={14} strokeWidth={1.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="mt-8">
          {orders.length === 0 ? (
            <p className="font-body text-muted-foreground">{t.admin.noOrders}</p>
          ) : (
            <div className="border border-border rounded-2xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-cream/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="px-5 py-3">#</th>
                    <th className="px-5 py-3">Toy</th>
                    <th className="px-5 py-3">Buyer</th>
                    <th className="px-5 py-3">Email</th>
                    <th className="px-5 py-3">$</th>
                    <th className="px-5 py-3">Date</th>
                    <th className="px-5 py-3">{t.admin.orderStatus}</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-t border-border text-sm hover:bg-cream/30 transition-colors">
                      <td className="px-5 py-4 font-mono text-xs">
                        <button
                          onClick={() => setViewingOrder(o)}
                          data-testid={`admin-order-link-${o.id}`}
                          className="text-terracotta hover:underline inline-flex items-center gap-1.5"
                        >
                          <Eye size={12} strokeWidth={1.5} />
                          {o.order_number}
                        </button>
                      </td>
                      <td className="px-5 py-4 font-serif-display text-base">{o.toy_snapshot?.name}</td>
                      <td className="px-5 py-4">{o.buyer_first_name} {o.buyer_last_name}</td>
                      <td className="px-5 py-4 text-muted-foreground">{o.buyer_email}</td>
                      <td className="px-5 py-4 text-terracotta">${o.total_price}</td>
                      <td className="px-5 py-4 text-muted-foreground">
                        {new Date(o.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-4">
                        <StatusSelect
                          status={o.status}
                          t={t}
                          onChange={async (st) => {
                            await api.patch(`/orders/${o.id}/status`, { status: st });
                            toast.success("OK");
                            loadAll();
                          }}
                          testid={`admin-status-${o.id}`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {viewingOrder && (
        <OrderDetailModal
          order={viewingOrder}
          onClose={() => setViewingOrder(null)}
          t={t}
        />
      )}

      {showForm && (
        <ToyForm
          initial={editing || blankToy}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSave={onSave}
          t={t}
        />
      )}
    </div>
  );
};

const ToyForm = ({ initial, onClose, onSave, t }) => {
  const [data, setData] = useState(initial);
  const [aiBusy, setAiBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setData((d) => ({ ...d, [k]: v }));
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const dataUris = await Promise.all(
        files.map(
          (f) =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(f);
            })
        )
      );
      setData((d) => ({ ...d, gallery: [...(d.gallery || []), ...dataUris] }));
    } catch (err) {
      toast.error("Erreur lors de l'import des images");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => {
    setData((d) => ({ ...d, gallery: d.gallery.filter((_, i) => i !== idx) }));
  };

  const askRandom = async () => {
    setAiBusy(true);
    try {
      const colors = (data.colors || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await api.post("/admin/generate-suggestions", {
        species_fr: data.species_fr || "",
        species_en: data.species_en || "",
        colors,
        current_name: data.name || "",
      });
      const s = res.data;
      setData((d) => ({
        ...d,
        name: s.name || d.name,
        description_fr: s.description_fr || d.description_fr,
        description_en: s.description_en || d.description_en,
        personality_fr: s.personality_fr || d.personality_fr,
        personality_en: s.personality_en || d.personality_en,
      }));
      toast.success("Suggestions générées !");
    } catch (err) {
      console.error(err);
      toast.error("L'IA n'a pas pu répondre. Réessayez.");
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-background rounded-2xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-background z-10">
          <h3 className="font-serif-display text-2xl">{initial.id ? t.admin.editToy : t.admin.addToy}</h3>
          <button onClick={onClose} className="p-2 hover:bg-cream rounded-full" data-testid="form-close">
            <X size={16} />
          </button>
        </div>
        <form
          className="p-6 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSave(data);
          }}
        >
          <div className="grid grid-cols-2 gap-3">
            <Inp label={`${t.admin.species} FR`} value={data.species_fr} onChange={set("species_fr")} required />
            <Inp label={`${t.admin.species} EN`} value={data.species_en} onChange={set("species_en")} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Inp label={t.admin.price} type="number" step="0.01" value={data.price} onChange={set("price")} required />
            <Inp label={t.admin.hours} type="number" value={data.creation_hours} onChange={set("creation_hours")} required />
          </div>
          <Inp label={t.admin.birth} value={data.birth_date} onChange={set("birth_date")} placeholder="Mars 2026" />
          <Inp label={t.admin.colors} value={data.colors} onChange={set("colors")} />

          {/* IMAGES UPLOAD */}
          <div className="block">
            <span className="block text-xs text-ink/70 mb-1.5 font-body">{t.admin.images}</span>
            <div className="border border-dashed border-border rounded-lg p-4 bg-cream/40">
              {data.gallery && data.gallery.length > 0 ? (
                <div className="grid grid-cols-4 gap-3 mb-3">
                  {data.gallery.map((src, i) => (
                    <div key={i} className="relative group aspect-square rounded-md overflow-hidden border border-border">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        data-testid={`remove-image-${i}`}
                        className="absolute top-1 right-1 w-6 h-6 bg-ink/80 text-background rounded-full grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Supprimer"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground mb-3">
                  Aucune image. Une image « bientôt disponible » sera utilisée par défaut.
                </p>
              )}
              <label className="inline-flex items-center gap-2 cursor-pointer px-4 py-2 rounded-full border border-border hover:border-terracotta text-sm bg-background transition-colors">
                <ImagePlus size={14} strokeWidth={1.5} />
                {uploading ? "Import…" : t.admin.uploadImages}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFiles}
                  className="hidden"
                  data-testid="form-upload-images"
                />
              </label>
            </div>
          </div>

          {/* NAME + RANDOM (placed right above descriptions) */}
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Inp label={t.admin.name} value={data.name} onChange={set("name")} required testid="form-name" />
            </div>
            <button
              type="button"
              onClick={askRandom}
              disabled={aiBusy}
              data-testid="form-random"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-camel text-ink hover:bg-terracotta hover:text-background text-sm whitespace-nowrap disabled:opacity-60 transition-colors border border-camel"
              title="Générer des suggestions avec l'IA"
            >
              {aiBusy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} strokeWidth={1.5} />}
              Random
            </button>
          </div>

          <Inp as="textarea" rows={3} label={t.admin.descFr} value={data.description_fr} onChange={set("description_fr")} />
          <Inp as="textarea" rows={3} label={t.admin.descEn} value={data.description_en} onChange={set("description_en")} />
          <Inp label={t.admin.personFr} value={data.personality_fr} onChange={set("personality_fr")} />
          <Inp label={t.admin.personEn} value={data.personality_en} onChange={set("personality_en")} />
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={data.available} onChange={set("available")} /> {t.admin.available}
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={data.featured} onChange={set("featured")} /> {t.admin.featured}
            </label>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <button type="button" onClick={onClose} className="px-5 py-2 rounded-full border border-border text-sm">
              {t.admin.cancel}
            </button>
            <button type="submit" data-testid="form-save" className="px-5 py-2 rounded-full bg-terracotta text-background text-sm">
              {t.admin.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Inp = ({ as = "input", label, testid, ...rest }) => {
  const Tag = as;
  return (
    <label className="block">
      <span className="block text-xs text-ink/70 mb-1.5 font-body">{label}</span>
      <Tag
        data-testid={testid}
        className="w-full bg-cream/40 border border-border rounded-lg px-4 py-2.5 font-body text-sm outline-none focus:border-terracotta"
        {...rest}
      />
    </label>
  );
};

// ============== ORDER STATUS + DETAILS ==============

const STATUS_OPTIONS = ["ordered", "paid", "shipped", "completed"];

const STATUS_STYLES = {
  ordered: "bg-amber-100 text-amber-900 border-amber-300",
  paid: "bg-sky-100 text-sky-900 border-sky-300",
  shipped: "bg-indigo-100 text-indigo-900 border-indigo-300",
  completed: "bg-emerald-100 text-emerald-900 border-emerald-300",
};

const statusLabel = (st, t) => {
  const k = "status" + st.charAt(0).toUpperCase() + st.slice(1);
  return t.admin[k] || st;
};

const StatusSelect = ({ status, onChange, t, testid }) => {
  const safe = STATUS_OPTIONS.includes(status) ? status : "ordered";
  return (
    <select
      data-testid={testid}
      value={safe}
      onChange={(e) => onChange(e.target.value)}
      className={`border rounded-full px-3 py-1 text-xs font-body cursor-pointer ${STATUS_STYLES[safe]}`}
    >
      {STATUS_OPTIONS.map((st) => (
        <option key={st} value={st}>{statusLabel(st, t)}</option>
      ))}
    </select>
  );
};

const OrderDetailModal = ({ order, onClose, t }) => {
  const certRef = useRef(null);

  const print = () => {
    const node = certRef.current;
    if (!node) return;
    const html = node.outerHTML;
    const stylesheets = Array.from(
      document.querySelectorAll('link[rel="stylesheet"], style')
    )
      .map((s) => s.outerHTML)
      .join("");
    const win = window.open("", "_blank", "width=820,height=1120");
    if (!win) {
      toast.error("Pop-up bloqué — autorisez les fenêtres pour imprimer.");
      return;
    }
    win.document.write(`
<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="utf-8" />
<title>Certificat d'adoption ${order.certificate_number}</title>
${stylesheets}
<style>
  @page { size: A4 portrait; margin: 14mm; }
  html, body { margin: 0; padding: 0; background: white; }
  body { padding: 20px; display: flex; justify-content: center; align-items: flex-start; }
  /* Strip the cream paper texture — pre-printed colored paper will provide the warmth */
  .certificate-bg {
    background: white !important;
    background-color: white !important;
    background-image: none !important;
    box-shadow: 0 0 0 1px #4A2E20 !important;
  }
  /* keep the certificate fitting one page */
  .certificate-bg { max-width: 680px !important; width: 100% !important; }
</style>
</head>
<body>${html}</body>
</html>`);
    win.document.close();
    win.focus();
    // Give the new window a beat to load fonts + stylesheets, then print
    setTimeout(() => {
      try {
        win.print();
      } catch (e) {
        console.error(e);
      }
    }, 900);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
      data-testid="order-detail-modal"
    >
      <div className="bg-background rounded-2xl border border-border w-full max-w-4xl my-8">
        {/* Header */}
        <div className="no-print flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h3 className="font-serif-display text-2xl text-ink">{t.admin.orderDetails}</h3>
            <p className="font-mono text-xs text-muted-foreground mt-0.5">{order.order_number}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={print}
              data-testid="order-print"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-terracotta text-background text-sm hover:shadow-warm transition-all"
            >
              <Printer size={14} strokeWidth={1.5} /> {t.admin.printCertificate}
            </button>
            <button onClick={onClose} className="p-2 hover:bg-cream rounded-full" data-testid="order-close">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="no-print px-6 py-6 grid md:grid-cols-2 gap-8">
          <div className="space-y-6 text-sm">
            <Info label={t.admin.buyerInfo}>
              <Line k="Nom" v={`${order.buyer_first_name} ${order.buyer_last_name}`} />
              <Line k="Email" v={order.buyer_email} />
              <Line k="Tél." v={order.buyer_phone || "—"} />
            </Info>

            <Info label={t.admin.shippingAddress}>
              <Line k="" v={order.address_line1} />
              {order.address_line2 && <Line k="" v={order.address_line2} />}
              <Line k="" v={`${order.postal_code} ${order.city}`} />
              <Line k="" v={order.country} />
            </Info>

            <Info label={t.admin.giftInfo}>
              <Line k={t.adopt.isGift} v={order.is_gift ? t.admin.yes : t.admin.no} />
              {order.is_gift && (
                <>
                  <Line k={t.adopt.giftRecipient} v={order.gift_recipient || "—"} />
                  <Line k={t.adopt.message} v={order.adoption_message || "—"} />
                </>
              )}
            </Info>

            <Info label={t.admin.orderStatus}>
              <Line k="" v={statusLabel(order.status || "ordered", t)} />
              <Line k="Date" v={new Date(order.created_at).toLocaleString()} />
              <Line k="Total" v={`$${order.total_price} CAD`} />
              <Line k="Toy" v={order.toy_snapshot?.name} />
            </Info>
          </div>

          {/* Certificate preview (also what gets printed) */}
          <div className="print-area">
            <CertificatePreview
              ref={certRef}
              toy={order.toy_snapshot}
              buyer={{
                firstName: order.buyer_first_name,
                lastName: order.buyer_last_name,
                isGift: order.is_gift,
                giftRecipient: order.gift_recipient,
              }}
              message={order.adoption_message}
              certificateNumber={order.certificate_number}
              dateOverride={order.created_at}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const Info = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">{label}</div>
    <div className="space-y-1.5 text-ink">{children}</div>
  </div>
);

const Line = ({ k, v }) => (
  <div className="flex items-baseline gap-2">
    {k && <span className="text-muted-foreground text-xs min-w-[60px]">{k}</span>}
    <span className="font-body text-sm">{v}</span>
  </div>
);

export default Admin;
